import { StateAbbreviation } from "@nimble/data-service-client/types";
import type { UserId } from "@nimble/datamodel/types/userId";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";

import {
  getSavedSearches,
  SavedSearchesScope,
} from "@/lib/api-clients/candidate-api-client";
import {
  ALL_STATES_ABBREVIATION,
  type StateConfigAbbreviation,
} from "@/lib/states-config";

export type GetSavedSearchesResponse = Awaited<
  ReturnType<typeof getSavedSearches>
>;

export type SavedSearchesParams = {
  state?: StateAbbreviation;
  stateless?: boolean;
  scope?: SavedSearchesScope;
};

const STATELESS_QUERY_KEY_SCOPE = "stateless";

function toQueryKeyScope(params?: SavedSearchesParams) {
  if (params?.scope) return params.scope;
  if (params?.stateless) return STATELESS_QUERY_KEY_SCOPE;
  return params?.state;
}

export function toSavedSearchesParams(
  abbreviation: StateConfigAbbreviation,
): SavedSearchesParams {
  if (abbreviation === ALL_STATES_ABBREVIATION) return { stateless: true };
  return { state: abbreviation };
}

export function getUserSavedSearchesQueryKey(
  userId?: UserId,
  params?: SavedSearchesParams,
) {
  const scope = toQueryKeyScope(params);
  return scope !== undefined
    ? (["user-saved-searches", userId, scope] as const)
    : (["user-saved-searches", userId] as const);
}

export function useUserSavedSearches(
  userId?: UserId,
  params?: SavedSearchesParams,
  options?: Omit<
    UseQueryOptions<GetSavedSearchesResponse>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: getUserSavedSearchesQueryKey(userId, params),
    queryFn: async () => {
      if (!userId) throw new Error("userId is required");

      return await getSavedSearches(userId, params);
    },
    ...options,
  });
}
